"use client";

import dynamic from "next/dynamic";
import { createContext, useContext, useState, type FocusEvent, type PointerEvent } from "react";

import {
  RASHI_CHECK_FIELD_IDS,
  RASHI_CHECK_FORM,
  RASHI_CHECK_FRAME,
} from "@/components/rashi/rashi-check-form";
import BirthToolFormShell, {
  type BirthToolFormShellProps,
} from "@/components/tool-shell/BirthToolFormShell";
import type { RashiId } from "@ishvaram/domain-data/rashis";

import { RASHIFAL_CHECK_HI_FORM } from "./rashifal-check-hi-form";
import {
  RASHIFAL_HUB_CHECK_HI_RESERVE,
  RASHIFAL_HUB_CHECK_RESERVE,
} from "./rashifal-hub-check-form";

// The daily hubs' check boundary, the retired /rashifal/<sign>/ check's pattern
// (RashifalCheckLazy): the studio imports only this module, and it owns the
// one dynamic() to the island, which serves both locales. The form, place
// search, kundali client, verdict and analytics load as an async chunk; the
// first load gains only this file, the shell and the two locales' form props.
//
// Activation is element-scoped and local: no window listener and no timer, so
// a reader browsing the studio never downloads the island and the deploy's
// body pointerdown smoke loads nothing; a press, focus or submit on the shell
// does.
//
// The slot reserves the locale's tallest state and every state fills it, so
// neither shell -> form nor a slow engine reply -> answer (or error) moves the
// page below. The form is the retired /rashifal/<sign>/ check's, in the same max-w-md
// frame at the page's width, so its heights are that check's (measured
// 2026-10-14, next dev + Playwright, POST /kundali mocked, 7 widths from 320
// to 1280): in English the tallest state is the engine's place error after a
// typed place nobody picked, 560 at 320-390 and 452 from 640; in Hindi the
// engine failure, 522 at 320-390 and 446 from 640 (a typed place nobody picked
// never reaches the engine there: requireResolvedPlace). The hub answers,
// measured 2026-10-22 on both hubs (next dev + Playwright, POST /kundali
// mocked with a 1.5 s delay, 5 widths from 320 to 1280): the tallest is
// Vrishchik (Scorpio) with a boundary note naming Dhanu (Sagittarius), 418 at
// 320-360, 375 at 414 and 349 from 640 in English; 351 at 320, 308 at 414 and
// 303 from 640 in Hindi. No answer outgrows the form's error state, so one
// reserve per locale holds: 600/500 px in English and 560/480 px in Hindi,
// from the sm breakpoint for the second number. With them the slot held its
// height from shell to form to answer (or engine failure) in all 30 runs, and
// no layout shift counted after the submit.
// translate="no" covers the shell, form and answer, which re-render on "check
// another" (tests/translate-safe-islands.test.ts).

type CheckLanguage = "en" | "hi";

/** Each locale's shell (the live form's words) and slot floor. */
const SLOT: Readonly<Record<CheckLanguage, { shell: BirthToolFormShellProps; reserve: string }>> = {
  en: {
    shell: { ...RASHI_CHECK_FORM, className: RASHI_CHECK_FRAME },
    reserve: RASHIFAL_HUB_CHECK_RESERVE,
  },
  hi: {
    shell: { ...RASHIFAL_CHECK_HI_FORM, className: RASHI_CHECK_FRAME },
    reserve: RASHIFAL_HUB_CHECK_HI_RESERVE,
  },
};

/**
 * The shell's props for this page's language. dynamic()'s loading placeholder
 * receives no props from the page, so the slot hands them down through
 * context (RashifalCheckLazy's ShellProps).
 */
const ShellProps = createContext<BirthToolFormShellProps>(SLOT.en.shell);

function LoadingShell() {
  return <BirthToolFormShell {...useContext(ShellProps)} />;
}

const RashifalHubCheck = dynamic(() => import("./RashifalHubCheck"), {
  ssr: false,
  loading: () => <LoadingShell />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: RASHI_CHECK_FIELD_IDS.date,
  tob: RASHI_CHECK_FIELD_IDS.time,
  place: RASHI_CHECK_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || RASHI_CHECK_FIELD_IDS.date;
}

export interface RashifalHubCheckLazyProps {
  language: CheckLanguage;
  /** Opens a sign's reading in the studio above (DailyGocharRashifalStudio). */
  onOpenRashi: (rashi: RashiId) => void;
}

export default function RashifalHubCheckLazy({ language, onOpenRashi }: RashifalHubCheckLazyProps) {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);
  const slot = SLOT[language];

  // The shell's own onActivate cannot tell a keyboard focus from a touch, so
  // the slot reads the intent in the capture phase: focus follows the visitor
  // into the real form after a keyboard focus, a mouse press or a submit, and
  // never after a touch, which may be the start of a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(RASHI_CHECK_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-rashifal-hub-check-slot=""
      className={`flex flex-col ${slot.reserve}`}
      {...shellIntent}
    >
      {active ? (
        <ShellProps.Provider value={slot.shell}>
          <RashifalHubCheck
            language={language}
            onOpenRashi={onOpenRashi}
            focusFieldId={focusFieldId}
          />
        </ShellProps.Provider>
      ) : (
        <BirthToolFormShell {...slot.shell} onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
